import { create } from 'zustand'
import { apiClient } from '@/lib/api.ts'
import { MODULE_DEFINITIONS, moduleStatusKey } from '@shared/types'
import type { ModuleStatus } from '@shared/types'

interface ModuleStatusMap {
  [moduleId: string]: ModuleStatus
}

interface ModulesState {
  statuses: ModuleStatusMap
  loaded: boolean
  loading: boolean
  // Per-user module access
  isAdmin: boolean
  accessOverrides: Record<string, boolean>
  accessLoaded: boolean
  accessLoading: boolean
  fetchStatuses: () => Promise<void>
  fetchAccess: () => Promise<void>
  resetAccess: () => void
  setModuleStatus: (moduleId: string, status: ModuleStatus) => Promise<void>
  getStatus: (moduleId: string) => ModuleStatus
  isBeta: (moduleId: string) => boolean
  isAllowed: (moduleId: string) => boolean
}

export const useModulesStore = create<ModulesState>((set, get) => ({
  statuses: {},
  loaded: false,
  loading: false,
  isAdmin: false,
  accessOverrides: {},
  accessLoaded: false,
  accessLoading: false,

  fetchStatuses: async () => {
    if (get().loading) return
    set({ loading: true })
    try {
      const { data } = await apiClient.get('/settings/modules')
      set({ statuses: data, loaded: true })
    } catch {
      // On error, fall back to defaults
      const defaults: ModuleStatusMap = {}
      for (const mod of MODULE_DEFINITIONS) {
        defaults[mod.id] = mod.defaultStatus
      }
      set({ statuses: defaults, loaded: true })
    } finally {
      set({ loading: false })
    }
  },

  fetchAccess: async () => {
    if (get().accessLoading) return
    set({ accessLoading: true })
    try {
      const { data } = await apiClient.get('/users/me/modules')
      set({
        isAdmin: !!data.isAdmin,
        accessOverrides: data.overrides ?? {},
        accessLoaded: true,
      })
    } catch {
      // On error: fall back to "everything allowed" (default-allow)
      set({ isAdmin: false, accessOverrides: {}, accessLoaded: true })
    } finally {
      set({ accessLoading: false })
    }
  },

  resetAccess: () => {
    set({
      isAdmin: false,
      accessOverrides: {},
      accessLoaded: false,
      accessLoading: false,
    })
  },

  setModuleStatus: async (moduleId: string, status: ModuleStatus) => {
    await apiClient.put('/settings/modules', { [moduleStatusKey(moduleId)]: status })
    set((state) => ({
      statuses: { ...state.statuses, [moduleId]: status },
    }))
  },

  getStatus: (moduleId: string): ModuleStatus => {
    const s = get().statuses[moduleId]
    if (s) return s
    const mod = MODULE_DEFINITIONS.find((m) => m.id === moduleId)
    return mod?.defaultStatus ?? 'stable'
  },

  isBeta: (moduleId: string): boolean => {
    return get().getStatus(moduleId) === 'beta'
  },

  isAllowed: (moduleId: string): boolean => {
    const state = get()
    if (state.isAdmin) return true
    // Default-deny: only explicitly allowed modules are accessible.
    return state.accessOverrides[moduleId] === true
  },
}))
